import React, { useState, useMemo, useEffect } from "react";
import { StyleSheet, ScrollView, View, Pressable } from "react-native";
import { router, useLocalSearchParams } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase-client";
import { DEFAULT_PASSWORD_REQUIREMENTS } from "@/types/db/tenants";
import useToast from "@/hooks/useToast";

export default function NewPasswordScreen() {
  const { colors } = useTheme();
  const { tenant: tenantId } = useLocalSearchParams<{ tenant: string }>();
  const toast = useToast();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tenantRequirements, setTenantRequirements] = useState(DEFAULT_PASSWORD_REQUIREMENTS);
  const [loadingRequirements, setLoadingRequirements] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch tenant password requirements on mount
  useEffect(() => {
    const fetchRequirements = async () => {
      if (!tenantId) {
        setLoadingRequirements(false);
        return;
      }

      try {
        const { data, error } = await supabase.from("tenants").select("meta_data").eq("id", tenantId).single();

        if (!error && data?.meta_data?.password_policy?.requirements) {
          setTenantRequirements({
            ...DEFAULT_PASSWORD_REQUIREMENTS,
            ...data.meta_data.password_policy.requirements,
          });
        }
      } catch (err) {
        console.warn("Failed to fetch tenant password requirements, using defaults");
      } finally {
        setLoadingRequirements(false);
      }
    };
    fetchRequirements();
  }, [tenantId]);

  // Password validation based on tenant requirements
  const requirements = useMemo(
    () => ({
      minLength: password.length >= tenantRequirements.min_length,
      hasUppercase: !tenantRequirements.require_uppercase || /[A-Z]/.test(password),
      hasLowercase: !tenantRequirements.require_lowercase || /[a-z]/.test(password),
      hasNumber: !tenantRequirements.require_number || /[0-9]/.test(password),
      hasSpecial: !tenantRequirements.require_special || /[!@#$%^&*(),.?":{}|<>]/.test(password),
    }),
    [password, tenantRequirements],
  );

  const allRequirementsMet = Object.values(requirements).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allRequirementsMet && passwordsMatch;

  const getInputBorderColor = (value: string, isValid: boolean) => {
    if (!value) return colors.border;
    return isValid ? colors.emerald?.border || colors.primary : colors.ruby?.border || "red";
  };

  const handleCreatePassword = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);

    try {
      // Get current session for auth header
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("Not authenticated");
      }

      // Call edge function to update password (uses service role to bypass RLS)
      const { data, error } = await supabase.functions.invoke("update-password", {
        body: {
          newPassword: password,
          isFirstTimeSetup: true,
        },
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to set password");
      }

      if (!data?.success) {
        throw new Error(data?.message || "Failed to set password");
      }

      // Refresh session to get updated JWT (needs_password_update = false)
      // AuthProvider's onAuthStateChange listener will automatically update state
      await supabase.auth.refreshSession();

      toast.showToast({
        title: "Password Set",
        message: "Your password has been created successfully",
        toastType: "success",
      });

      // Navigate to app
      router.replace("/");
    } catch (error: any) {
      console.error("Password update error:", error);
      toast.showToast({
        title: "Error",
        message: error.message || "Failed to set password",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.content}>
        <ThemedText style={[styles.title, { color: colors.text }]}>Set Your Password</ThemedText>
        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
          Create a secure password to get started
        </ThemedText>

        {/* Password Inputs */}
        <View style={styles.formSection}>
          <View style={styles.inputContainer}>
            <Input
              icon="lock"
              placeholder="Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              containerStyle={[styles.input, { borderColor: getInputBorderColor(password, allRequirementsMet) }]}
            />
            <Pressable style={styles.eyeButton} onPress={() => setShowPassword(!showPassword)}>
              <MaterialIcons
                name={showPassword ? "visibility" : "visibility-off"}
                size={20}
                color={colors.textSecondary}
              />
            </Pressable>
          </View>

          <View style={styles.inputContainer}>
            <Input
              icon="lock"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry={!showConfirmPassword}
              containerStyle={[styles.input, { borderColor: getInputBorderColor(confirmPassword, passwordsMatch) }]}
            />
            <Pressable style={styles.eyeButton} onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
              <MaterialIcons
                name={showConfirmPassword ? "visibility" : "visibility-off"}
                size={20}
                color={colors.textSecondary}
              />
            </Pressable>
          </View>

          {confirmPassword.length > 0 && !passwordsMatch && (
            <View style={styles.errorRow}>
              <MaterialIcons name="error" size={16} color={colors.ruby?.text || "red"} />
              <ThemedText style={[styles.errorText, { color: colors.ruby?.text || "red" }]}>
                Passwords do not match
              </ThemedText>
            </View>
          )}
        </View>

        {/* Requirements Card */}
        <View style={[styles.requirementsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.requirementsTitle, { color: colors.text }]}>Password Requirements</ThemedText>

          <RequirementRow
            met={requirements.minLength}
            text={`At least ${tenantRequirements.min_length} characters`}
            colors={colors}
          />
          {tenantRequirements.require_uppercase && (
            <RequirementRow met={requirements.hasUppercase} text="One uppercase letter" colors={colors} />
          )}
          {tenantRequirements.require_lowercase && (
            <RequirementRow met={requirements.hasLowercase} text="One lowercase letter" colors={colors} />
          )}
          {tenantRequirements.require_number && (
            <RequirementRow met={requirements.hasNumber} text="One number" colors={colors} />
          )}
          {tenantRequirements.require_special && (
            <RequirementRow met={requirements.hasSpecial} text="One special character (!@#$%^&*)" colors={colors} />
          )}
        </View>

        {/* Completion Section */}
        <View style={[styles.completionSection, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <MaterialIcons
            name="celebration"
            size={32}
            color={canSubmit ? colors.emerald?.text || colors.primary : colors.textSecondary}
          />
          <ThemedText style={[styles.completionTitle, { color: colors.text }]}>Almost There!</ThemedText>
          <ThemedText style={[styles.completionText, { color: colors.textSecondary }]}>
            Choose a strong password to secure your account.
          </ThemedText>

          <Button
            text={saving ? "Saving..." : "Set Password"}
            onPress={handleCreatePassword}
            icon={saving ? undefined : "check"}
            iconSize={18}
            enabled={canSubmit && !saving}
            style={[
              styles.submitButton,
              {
                backgroundColor: canSubmit && !saving ? colors.emerald?.background || colors.primary : colors.border,
                opacity: canSubmit && !saving ? 1 : 0.5,
              },
            ]}
            textStyle={{ color: "white", fontSize: 16, fontWeight: "600" }}
          />
        </View>
      </View>
    </ScrollView>
  );
}

function RequirementRow({ met, text, colors }: { met: boolean; text: string; colors: any }) {
  return (
    <View style={styles.requirementRow}>
      <MaterialIcons
        name={met ? "check-circle" : "radio-button-unchecked"}
        size={18}
        color={met ? colors.emerald?.text || colors.primary : colors.textSecondary}
      />
      <ThemedText style={[styles.requirementText, { color: met ? colors.text : colors.textSecondary }]}>
        {text}
      </ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 32,
  },
  formSection: {
    gap: 16,
    marginBottom: 24,
  },
  inputContainer: {
    position: "relative",
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
  },
  eyeButton: {
    position: "absolute",
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    padding: 8,
  },
  errorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  errorText: {
    fontSize: 14,
  },
  requirementsCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginBottom: 24,
    gap: 12,
  },
  requirementsTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
  },
  requirementRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  requirementText: {
    fontSize: 14,
  },
  completionSection: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
    alignItems: "center",
    gap: 12,
  },
  completionTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  completionText: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 8,
  },
  submitButton: {
    borderRadius: 8,
    minHeight: 48,
    minWidth: 200,
  },
});
